import { ReportDefinition, ReportHeaderSnippet } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
function parseReportJson(reportJson: any): any {
   if (typeof reportJson !== 'string') return reportJson;

   try {
      return JSON.parse(reportJson);
   } catch {
      return {};
   }
}

function asString(value: any): string {
   if (value === null || value === undefined) return '';
   return String(value);
}

function asNumberOrNull(value: any): number | null {
   if (value === null || value === undefined || value === '') return null;

   const amount = Number(value);
   return Number.isFinite(amount) ? amount : null;
}

function formatCurrency(value: any): string {
   const amount = asNumberOrNull(value);

   if (amount === null) {
      return '';
   }

   const formatted = Math.abs(amount).toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
   });

   return amount < 0 ? `-$${formatted}` : `$${formatted}`;
}

function formatDate(value: any): string {
   const raw = asString(value).trim();

   if (!raw) {
      return '';
   }

   const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

   if (!match) {
      return raw;
   }

   return `${match[3]}/${match[2]}/${match[1]}`;
}

function getPmAccountRows(reportJson: any): any[] {
   const parsedReportJson = parseReportJson(reportJson);

   return Array.isArray(parsedReportJson?.PmAccounts)
      ? parsedReportJson.PmAccounts
      : [];
}

function getRowId(row: any): string {
   return (
      asString(row?.TyallsRowId) ||
      asString(row?.tyallsRowId) ||
      `pm:${asString(row?.RoomNumber)}:${asString(row?.Name)}:${asString(
         row?.Arrival
      )}:${asString(row?.Departure)}`
   );
}

function getBalance(row: any): number | null {
   return asNumberOrNull(row?.Balance);
}

function getCompany(row: any): string {
   return asString(row?.Company) || '-';
}

function getPersons(row: any): string {
   const persons = row?.Persons;

   if (persons === null || persons === undefined || persons === '') {
      return '';
   }

   return asString(persons);
}

function hasNonZeroBalance(row: any): boolean {
   const balance = getBalance(row);
   return balance !== null && balance !== 0;
}

function flattenRows(reportJson: any): ReportRowVm[] {
   return [...getPmAccountRows(reportJson)]
      .sort((a, b) => {
         return (
            asString(a?.Name).localeCompare(asString(b?.Name)) ||
            asString(a?.RoomNumber).localeCompare(asString(b?.RoomNumber))
         );
      })
      .map((row) => {
         const balance = getBalance(row);
         const days = getDaysSinceArrival(row?.Arrival);

         return {
            rowId: getRowId(row),
            cells: {
               rowType: 'pmAccount',

               name: asString(row?.Name),
               roomNumber: asString(row?.RoomNumber),
               company: getCompany(row),

               arrival: formatDate(row?.Arrival),
               arrivalRaw: asString(row?.Arrival),

               days: days === null ? '' : days,
               daysRaw: days,

               departure: formatDate(row?.Departure),
               departureRaw: asString(row?.Departure),

               roomType: asString(row?.RoomType),
               persons: getPersons(row),
               personsRaw: asNumberOrNull(row?.Persons),

               balance: formatCurrency(balance),
               balanceRaw: balance,

               balanceStatus: hasNonZeroBalance(row) ? 'Non-zero' : 'Zero'
            }
         };
      });
}

function getHeaderSnippets(reportJson: any): ReportHeaderSnippet[] {
   const rows = getPmAccountRows(reportJson);

   const nonZeroCount = rows.filter(hasNonZeroBalance).length;

   const totalBalance = rows.reduce((total, row) => {
      const balance = getBalance(row);
      return balance !== null ? total + balance : total;
   }, 0);

   return [
      {
         label: 'Count',
         value: rows.length,
         tone: rows.length > 0 ? 'info' : 'success'
      },
      {
         label: 'Non-Zero',
         value: nonZeroCount,
         tone: nonZeroCount > 0 ? 'warning' : 'success'
      },
      {
         label: 'Total Balance',
         value: formatCurrency(totalBalance),
         tone: totalBalance !== 0 ? 'danger' : 'success'
      }
   ];
}

function getDateOnlyUtcTime(value: any): number | null {
   const raw = asString(value).trim();

   if (!raw) {
      return null;
   }

   const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

   if (!match) {
      return null;
   }

   const year = Number(match[1]);
   const month = Number(match[2]) - 1;
   const day = Number(match[3]);

   return Date.UTC(year, month, day);
}

function getTodayDateOnlyUtcTime(): number {
   const today = new Date();

   return Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
}

function getDaysSinceArrival(arrivalDate: any): number | null {
   const arrivalUtcTime = getDateOnlyUtcTime(arrivalDate);

   if (arrivalUtcTime === null) {
      return null;
   }

   return Math.floor((getTodayDateOnlyUtcTime() - arrivalUtcTime) / ONE_DAY_IN_MS);
}

export const PM_ACCOUNTS_DEFINITION: ReportDefinition = {
   reportType: 'PmAccounts',
   version: 2,
   title: 'PM Accounts',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'PM Accounts',
      snippets: ({ reportData }) => getHeaderSnippets(reportData)
   },

   rowChecks: {
      enabled: false
   },

   annotations: {
      enabled: true,
      selectableRowTypes: ['pmAccount']
   },

   columns: [
      {
         key: 'name',
         header: 'NAME',
         width: '280px',
         maxChars: 90,
         sort: { type: 'string' }
      },
      {
         key: 'roomNumber',
         header: 'ROOM NO.',
         width: '110px',
         sort: { type: 'string' }
      },
      {
         key: 'company',
         header: 'COMPANY',
         width: '240px',
         maxChars: 80,
         sort: { type: 'string' }
      },
      {
         key: 'arrival',
         header: 'ARRIVAL',
         width: '120px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['arrivalRaw']
         }
      },
      {
         key: 'departure',
         header: 'DEPARTURE',
         width: '120px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['departureRaw']
         }
      },
      {
         key: 'days',
         header: 'DAYS OPEN',
         width: '90px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['daysRaw'],
            defaultDirection: 'desc'
         }
      },
      {
         key: 'roomType',
         header: 'ROOM TYPE',
         width: '110px',
         sort: { type: 'string' }
      },
      {
         key: 'persons',
         header: 'PRS.',
         width: '80px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['personsRaw']
         }
      },
      {
         key: 'balance',
         header: 'BALANCE',
         width: '130px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['balanceRaw'],
            defaultDirection: 'desc'
         }
      }
   ],

   flattenRows,

   filter: {
      fields: [
         { key: 'name', label: 'Name', type: 'string' },
         { key: 'roomNumber', label: 'Room No.', type: 'string' },
         { key: 'company', label: 'Company', type: 'string' },
         { key: 'arrival', label: 'Arrival', type: 'date' },
         { key: 'departure', label: 'Departure', type: 'date' },
         {
            key: 'days',
            label: 'Days',
            type: 'number',
            getValue: (row) => row.cells['daysRaw']
         },
         { key: 'roomType', label: 'Room Type', type: 'string' },
         { key: 'balanceStatus', label: 'Balance Status', type: 'string' },
         {
            key: 'balance',
            label: 'Balance',
            type: 'number',
            getValue: (row) => row.cells['balanceRaw']
         }
      ]
   }
};
